import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import _ from 'lodash';
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import './ExcelProcessing.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {useExcelProcessingListData} from '../../../hooks/ingestions/useIngestions';
import {PAGE_SIZE, ToastType} from '../../../shared/constants';
import {
  ExcelProcessingListItemType,
  ToastMessageConfigType,
} from '../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import CellWrapper from '../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
//import ESIndexErrorListModal from '../component/ESIndexErrorListModal/ESIndexErrorListModal';
import {formatIngestionDateTime} from '../ingestionAnalyticsUtils';
import ExcelProcessingErrorListModal from '../component/ExcelProcessingErrorListModal/ExcelProcessingErrorListModal';

const stringPath = 'ingestions.excelProcessing.';

const EXCEL_PROCESSING_STATUS_WITH_ERROR_TYPES = new Set([
  'ERROR',
  'COMPLETEDWE',
  'COMPLETEDWITHERRORS',
]);

type StringMap = Record<string, string>;
const excelProcessingStatusCaptionMap: StringMap = {
  COMPLETED: 'statusCompleted',
  COMPLETEDWE: 'statusCompletedWithErrors',
  COMPLETEDWITHERRORS: 'statusCompletedWithErrors',
  DATCREATED: 'statusDateCreated',
  ERROR: 'statusError',
  INPROGRESS: 'statusInProgress',
  PENDING: 'statusPending',
};

const defaultSortColumn = {
  field: 'startTime',
  sort: 'desc',
};

const formatExcelCount = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }
  return Number(value).toLocaleString();
};

const ExcelProcessing = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    defaultSortColumn,
  ] as GridSortModel);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });
  const [selectedErrorExcelProcessingId, setSelectedErrorExcelProcessingId] =
    useState<number | null>(null);

  const listRequest = useMemo(
    () => ({
      pageNumber,
      pageSize: itemPerPage,
      sortingColumn: sortModel[0]?.field ?? defaultSortColumn.field,
      direction: sortModel[0]?.sort ?? defaultSortColumn.sort,
    }),
    [pageNumber, itemPerPage, sortModel]
  );

  const {
    data: excelProcessingListData,
    isLoading,
    isError,
    error,
  } = useExcelProcessingListData(listRequest);

  useEffect(() => {
    if (isError && error) {
      setToastMsgConfig({
        show: true,
        message: t(stringPath + 'toastMsg.loadingFailed'),
        type: ToastType.Error,
      });
    }
  }, [isError, error]);

  const showToast = useCallback((config: ToastMessageConfigType) => {
    setToastMsgConfig({
      show: true,
      title: config.title,
      message: config.message,
      type: config.type,
    });
  }, []);

  const handleExcelProcessingClick = (params: GridCellParams) => {
    const {field, row}: {field: string; row: ExcelProcessingListItemType} =
      params;

    if (
      field === 'status' &&
      EXCEL_PROCESSING_STATUS_WITH_ERROR_TYPES.has(row.status)
    ) {
      setSelectedErrorExcelProcessingId(row.nativeExcelIngestionID);
    }
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'ingestionName',
        headerName: t(stringPath + 'ingestionName'),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <CellWrapper
            toolTipContent={params.value}
            cellParams={params}
            dataTestId={`excel-processing-ingestion-name-${params.id}`}
          >
            <span
              data-testid={`excel-processing-ingestion-name-text-${params.id}`}
            >
              {params.value || '--'}
            </span>
          </CellWrapper>
        ),
      },
      {
        field: 'excelCount',
        headerName: t(stringPath + 'excelCount'),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const {excelCount} = params.row as ExcelProcessingListItemType;
          return (
            <span data-testid={`excel-processing-excel-count-${params.id}`}>
              {formatExcelCount(excelCount)}
            </span>
          );
        },
      },
      {
        field: 'startTime',
        headerName: t('ingestions.common.startTime'),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const {startTime} = params.row as ExcelProcessingListItemType;
          const formattedStartTime = formatIngestionDateTime(startTime);

          return (
            <span data-testid={`excel-processing-start-time-${params.id}`}>
              {formattedStartTime}
            </span>
          );
        },
      },
      {
        field: 'endTime',
        headerName: t('ingestions.common.endTime'),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const {endTime} = params.row as ExcelProcessingListItemType;
          const formattedEndTime = formatIngestionDateTime(endTime);

          return (
            <span data-testid={`excel-processing-end-time-${params.id}`}>
              {formattedEndTime}
            </span>
          );
        },
      },
      {
        field: 'status',
        headerName: t('ingestions.common.status'),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const {status} = params.row as ExcelProcessingListItemType;
          let statusCaption = status;
          let className = '';

          // eslint-disable-next-line security/detect-object-injection
          if (excelProcessingStatusCaptionMap[status]) {
            statusCaption = t(
              // eslint-disable-next-line security/detect-object-injection
              stringPath + excelProcessingStatusCaptionMap[status]
            );
          }

          if (EXCEL_PROCESSING_STATUS_WITH_ERROR_TYPES.has(status)) {
            className = 'status-error-caption';
          }

          return (
            <CellWrapper
              toolTipContent={statusCaption}
              cellParams={params}
              dataTestId={`excel-processing-status-${params.id}`}
            >
              <span
                className={className}
                data-testid={`excel-processing-status-text-${params.id}`}
              >
                {statusCaption || '--'}
              </span>
            </CellWrapper>
          );
        },
      },
      {
        field: 'errorMessage',
        headerName: t('ingestions.common.errorMessage'),
        headerClassName: 'last-column',
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const {errorMessage} = params.row as ExcelProcessingListItemType;
          return (
            <CellWrapper
              toolTipContent={params.value}
              cellParams={params}
              dataTestId={`excel-processing-error-cell-wrapper-${params.id}`}
            >
              <span
                className="error-message-caption"
                data-testid={`excel-processing-error-message-${params.id}`}
              >
                {_.isEmpty(errorMessage) ? '--' : errorMessage}
              </span>
            </CellWrapper>
          );
        },
      },
    ],
    [t]
  );

  const dataGridClass = useMemo(() => {
    if (isLoading) return 'index-grid-loading';

    if ((excelProcessingListData?.totalRecords ?? 0) === 0) {
      return 'index-grid-no-row-overlay';
    }

    return 'index-grid';
  }, [isLoading, excelProcessingListData?.totalRecords]);

  return (
    <Box
      className="excel-processing-container"
      data-testid="excel-processing-container"
    >
      <Box
        className="excel-processing-grid-container"
        data-testid="excel-processing-grid-container"
      >
        <ArrDataGrid
          data-testid="excel-processing-data-grid"
          rows={excelProcessingListData?.data ?? []}
          rowCount={excelProcessingListData?.totalRecords ?? 0}
          columns={columns}
          paginationMode="server"
          primaryKey="nativeExcelIngestionID"
          loading={isLoading}
          showPerPageOptions
          pageSizeOptions={[10, 25, 50, 100]}
          itemsPerPage={itemPerPage}
          onPageChange={setPageNumber}
          onSortModelChange={setSortModel}
          currentPage={pageNumber}
          setItemsPerPage={setItemPerPage}
          onCellClick={handleExcelProcessingClick}
          onCellKeyDown={(params, event) => {
            if (event.key === 'Enter') {
              handleExcelProcessingClick(params);
            }
          }}
          entityType={t('ingestions.common.entries')}
          className={dataGridClass}
          hideFooter={(excelProcessingListData?.totalRecords ?? 0) === 0}
          initialState={{
            sorting: {
              sortModel: [defaultSortColumn] as GridSortModel,
            },
          }}
          alwaysShowPagination
          noRowsOverlayCustomText={t(stringPath + 'noDataOverlay')}
        />
      </Box>
      {selectedErrorExcelProcessingId && (
        <ExcelProcessingErrorListModal
          showModal
          nativeExcelIngestionID={selectedErrorExcelProcessingId}
          onClose={() => {
            setSelectedErrorExcelProcessingId(null);
          }}
          showToast={showToast}
          showExportSuccessToast={(config) => {
            showToast(config);
            setSelectedErrorExcelProcessingId(null);
          }}
        />
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Error]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
    </Box>
  );
};

export default ExcelProcessing;
